// Phase 0b PR 6: Award Comparison workspace.
//
// Routed at /delivery/pre-award/:id/comparison/:csId. Pulls the
// discrepancy roster off zou.ListChangeSetDiscrepancies (re-uses the
// AwardComparisonSummary shape BeginAwardComparison emits), groups by
// SDS §4 comparison_area, and lets reviewers resolve or re-route each
// row in a side drawer.
//
// Review items (D93: facts the award extraction couldn't settle, e.g. a
// contract-type conflict) are pulled out of the area table into the shared
// "Needs your call" group and open in the shared review panel, the same as
// ModificationCenter.
//
// The "Commit Import" CTA in the sticky footer is gated by two rules
// (per spec §Authorization / "Server-side invariants"):
//   1. every discrepancy is settled (not Open or Routed), AND
//   2. every Blocking-severity row carries a settled disposition
//      (Accepted | Corrected | Waived | Closed | Applied | Acknowledged).
// The button stays disabled until both hold.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraDataTable, SpecraDateField, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraListPage, SpecraOverlayDrawer, SpecraPageHeader, SpecraSectionHeader, SpecraSelect, SpecraTag, SpecraText, SpecraTextarea, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody } from '../../../components/layout';
import { ErrorState } from '../_shared/components/PageStates';
import { ReviewSourceChip } from '../_shared/components/ReviewSourceChip';
import { RESOLUTION_BADGE_VARIANTS, SEVERITY_BADGE_VARIANTS } from '../_shared/components/review/badgeVariants';
import { ReviewItemList } from '../_shared/components/review/ReviewItemList';
import { ReviewItemDrawer } from '../_shared/components/review/ReviewItemPanel';
import { gateNote, hasNoWriter, isReviewItem, NO_WRITER_NOTE, noWriterNeedsDecision, offeredDecisions, resolutionLabel } from '../_shared/components/review/reviewItemCopy';
import { isMoneyField } from '../_shared/lib/correctedValue';
import { formatMoney } from '../_shared/lib/recordLabels';
import { hasReviewSource, reviewSourceProps } from '../_shared/lib/reviewSourceProps';
import { useChangeSetDiscrepancies, useCommitImport, useResolveDiscrepancy, useRouteDiscrepancy } from './awardCompareHooks';
import { usePreAwardWorkspace } from './hooks';
// Reviewer decisions offered in the resolve drawer (sent to ResolveDiscrepancy).
// Canonical §8.2 vocabulary — the award-import flow runs on ContractChangeItem.
const RESOLUTION_DECISIONS = [
    'Accepted',
    'Corrected',
    'Waived',
    'Rejected',
    'Escalated'
];
// What a reviewer reads for a decision. Waived is "Leave blank": waiver has
// its own meaning in contracting, so the word is never shown.
const DECISION_LABELS = {
    Waived: 'Leave blank'
};
const decisionLabel = (s)=>DECISION_LABELS[s] ?? s;
// The decisions a row offers (RI-3b2). A correct-only row's staged value is
// missing or impossible, so accepting it as staged would refuse at commit:
// it settles by Correct or Leave blank only. A row no writer lands (SAD-700)
// settles only by a non-writing decision.
export function rowDecisions(d) {
    if (!d) return RESOLUTION_DECISIONS;
    if (hasNoWriter(d)) return offeredDecisions(d, RESOLUTION_DECISIONS);
    return d.correctOnly ? RESOLUTION_DECISIONS.filter((s)=>s !== 'Accepted') : RESOLUTION_DECISIONS;
}
// The decision a row's drawer opens on: the first it offers that settles it.
export function defaultRowDecision(d) {
    if (d && hasNoWriter(d)) return 'Waived';
    return d?.correctOnly ? 'Corrected' : 'Accepted';
}
// A comparison-only row is acknowledged, not accepted: nothing is written.
export function rowDecisionLabel(d, s) {
    return s === 'Accepted' && d?.acknowledges ? 'Acknowledge' : decisionLabel(s);
}
// The field a row addresses: the locator is {fieldPath: objectId}.
export function rowField(d) {
    try {
        return Object.keys(JSON.parse(d.targetRecordLocatorJson || '{}'))[0] ?? '';
    } catch  {
        return '';
    }
}
const CORRECTED_SHAPES = {
    award_date: [
        {
            key: 'award_date',
            label: 'Award date',
            kind: 'date'
        }
    ],
    pop_range: [
        {
            key: 'pop_start',
            label: 'Period starts',
            kind: 'date'
        },
        {
            key: 'pop_end',
            label: 'Period ends',
            kind: 'date'
        }
    ],
    funded_ceiling: [
        {
            key: 'funded_amount',
            label: 'Funded amount',
            kind: 'money'
        },
        {
            key: 'total_ceiling',
            label: 'Total ceiling',
            kind: 'money'
        }
    ]
};
export function correctedParts(field) {
    return CORRECTED_SHAPES[field] ?? null;
}
// Dollars as the award states them: non-negative, at most two decimals. The
// input's min/step attributes are hints only, so the limit is enforced here.
const MONEY_INPUT = /^\d+(\.\d{1,2})?$/;
// The decided_value_json the typed parts make: '' until every part is filled
// with a readable value, so the submit gate (needsValue) holds until then. A
// single-part shape is the bare value; a pair is the object the writer reads.
// A date pair must not end before it starts.
export function correctedAwardJson(field, typed) {
    const parts = correctedParts(field);
    if (!parts) return '';
    const out = {};
    for (const p of parts){
        const raw = (typed[p.key] ?? '').trim();
        if (!raw) return '';
        if (p.kind === 'money') {
            if (!MONEY_INPUT.test(raw)) return '';
            out[p.key] = Number(raw);
        } else {
            out[p.key] = raw;
        }
    }
    if (parts.length === 2 && parts.every((p)=>p.kind === 'date')) {
        // ISO dates order as strings.
        if (String(out[parts[1].key]) < String(out[parts[0].key])) return '';
    }
    if (field === 'funded_ceiling' && Number(out.funded_amount) > Number(out.total_ceiling)) {
        return '';
    }
    return parts.length === 1 ? JSON.stringify(out[parts[0].key]) : JSON.stringify(out);
}
// Whether a reviewer's raw JSON parses; the typed parts always do.
export function isValidJson(raw) {
    try {
        const value = JSON.parse(raw);
        if (value === null) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return Object.keys(value).length > 0;
        return true;
    } catch  {
        return false;
    }
}
// An Acknowledged row's badge says why nothing changed.
export const ACKNOWLEDGED_TOOLTIP = 'Compared only: nothing is written to the contract';
// Full canonical resolution-state set for the filter dropdown (a row can be in
// any of these: Open initial, Routed by re-triage, the reviewer decisions,
// Applied/Closed terminal).
const RESOLUTION_STATES = [
    'Open',
    'Routed',
    'Accepted',
    'Corrected',
    'Waived',
    'Rejected',
    'Escalated',
    'Applied',
    'Acknowledged',
    'Closed'
];
const FUNCTIONAL_OWNERS = [
    'Delivery',
    'Contracts',
    'Finance',
    'Subcontracts',
    'HrOnboarding',
    'Quality',
    'Deliverables',
    'GrowthHandoff'
];
// Canonical SDS §4 ordering — used for group rendering so the table
// stays stable across runs even when the engine emits in declaration
// order. Mirrors awardcompare.defaultRulePacks().
const AREA_ORDER = [
    'ScopePws',
    'Deliverables',
    'PricingFunding',
    'StaffingKeyPersonnel',
    'VehicleTaskOrder',
    'SubkAtp',
    'DatesCalendar',
    'CustomerPocs',
    'ComplianceReporting'
];
export function AwardComparisonPage() {
    const { id: preAwardId, csId } = useParams();
    const navigate = useNavigate();
    // useParams returns `string | undefined`; an absent csId would
    // otherwise be coerced to '' and silently sent to the backend, where
    // ListChangeSetDiscrepancies would 400. The hook still runs (Rules
    // of Hooks) — but `enabled: !!changeSetId` keeps it idle when the
    // route is malformed, and the early <Navigate> below redirects out
    // before render.
    const changeSetId = csId ?? '';
    const { data, isLoading, isError, error } = useChangeSetDiscrepancies(changeSetId);
    // The pre-award's zou project holds the award document the review quotes
    // open in.
    const { data: workspace } = usePreAwardWorkspace(preAwardId ?? '');
    const zouProjectId = workspace?.zouProjectId ?? '';
    const resolve = useResolveDiscrepancy();
    const route = useRouteDiscrepancy();
    const commit = useCommitImport();
    const [openId, setOpenId] = useState(null);
    // The resolve / re-route form, held here so its actions ride the drawer
    // footer. Reset whenever a row opens.
    const [decision, setDecision] = useState('Accepted');
    const [rationale, setRationale] = useState('');
    const [decidedJson, setDecidedJson] = useState('');
    const [newOwner, setNewOwner] = useState('');
    const [openReviewId, setOpenReviewId] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [areaFilter, setAreaFilter] = useState('all');
    const [resolutionFilter, setResolutionFilter] = useState('all');
    const [severityFilter, setSeverityFilter] = useState('all');
    const discrepancies = data?.discrepancies ?? [];
    // Review items render only in their own group; the filters and area table
    // are for the comparison engine's rows.
    const reviewItems = useMemo(()=>discrepancies.filter(isReviewItem), [
        discrepancies
    ]);
    const filtered = useMemo(()=>{
        return discrepancies.filter((d)=>{
            if (isReviewItem(d)) return false;
            if (areaFilter !== 'all' && d.comparisonArea !== areaFilter) return false;
            if (resolutionFilter !== 'all' && d.resolutionState !== resolutionFilter) return false;
            if (severityFilter !== 'all' && d.severity !== severityFilter) return false;
            return true;
        });
    }, [
        discrepancies,
        areaFilter,
        resolutionFilter,
        severityFilter
    ]);
    const groups = useMemo(()=>groupByArea(filtered), [
        filtered
    ]);
    const open = useMemo(()=>discrepancies.find((d)=>d.id === openId) ?? null, [
        discrepancies,
        openId
    ]);
    const commitGate = useMemo(()=>computeCommitGate(discrepancies), [
        discrepancies
    ]);
    // The parent nulls `open` on close while the drawer animates out — snapshot
    // the last open discrepancy so the exit keeps showing its content.
    const lastOpen = useRef(open);
    if (open) lastOpen.current = open;
    const shownDiscrepancy = open ?? lastOpen.current;
    // Route guard — runs AFTER hooks (Rules of Hooks). When the URL is
    // missing either id, redirect to the pre-award detail (or /delivery
    // when even the pre-award id is gone) so the user lands somewhere
    // they can recover from.
    if (!csId || !preAwardId) {
        return /*#__PURE__*/ _jsx(Navigate, {
            to: preAwardId ? `/delivery/pre-award/${preAwardId}` : '/delivery',
            replace: true
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                tone: "muted",
                className: "p-8",
                "data-testid": Testids.awardCompare.loadingState,
                children: "Loading award comparison…"
            })
        });
    }
    if (isError || !data) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("div", {
                className: "p-8",
                "data-testid": Testids.awardCompare.errorState,
                children: /*#__PURE__*/ _jsx(ErrorState, {
                    title: "Award comparison unavailable",
                    error: error instanceof Error ? error : 'No change-set found for this id.'
                })
            })
        });
    }
    const openRow = (id)=>{
        const d = discrepancies.find((x)=>x.id === id);
        setDecision(defaultRowDecision(d ?? null));
        setRationale('');
        setDecidedJson('');
        setNewOwner(d?.functionalOwner ?? '');
        setOpenId(id);
    };
    const handleResolve = ()=>{
        if (!open) return;
        resolve.mutate({
            changeSetId,
            discrepancyId: open.id,
            resolutionState: decision,
            decisionRationale: rationale.trim(),
            decidedValueJson: decision === 'Corrected' ? decidedJson.trim() : ''
        }, {
            onSuccess: ()=>{
                setOpenId(null);
            }
        });
    };
    const handleRoute = ()=>{
        if (!open) return;
        route.mutate({
            changeSetId,
            discrepancyId: open.id,
            functionalOwner: newOwner
        }, {
            onSuccess: ()=>{
                toast.success(`Routed to ${newOwner}.`);
            }
        });
    };
    const onCommit = ()=>{
        commit.mutate({
            changeSetId
        }, {
            onSuccess: (resp)=>{
                // Backend reports success either via `summary` (commit
                // ran end-to-end) or `violations` (server-side
                // invariant rejected even though the client thought
                // the gate was open — could happen if another reviewer
                // raced ahead).
                if (resp.violations && resp.violations.length > 0) {
                    const detail = resp.violations.map((v)=>v.detail).filter(Boolean).join('; ');
                    toast.error(`Commit blocked: ${detail || 'invariant violation'}`);
                    return;
                }
                if (!resp.summary) {
                    toast.error('Commit returned an empty response.');
                    return;
                }
                toast.success('Contract project created.');
                navigate(`/delivery/programs/${resp.summary.contractProjectId}`);
            }
        });
    };
    // A correct-only row settles with the reviewer's own value, never the
    // staged one (zou's AcceptedWithoutCorrection) — and that value must parse,
    // since a field of no known shape is typed as JSON by hand.
    // A Corrected decision on a correct-only row, or on any row of a known
    // shape (typed inputs), must carry the value it writes.
    const needsValue = !!open && decision === 'Corrected' && (decidedJson.trim() === '' || !isValidJson(decidedJson.trim()));
    const canSubmit = rationale.trim().length > 0 && !needsValue && !resolve.isPending;
    const canRoute = !!open && newOwner !== open.functionalOwner && !route.isPending;
    return /*#__PURE__*/ _jsxs(PageBody, {
        children: [
            /*#__PURE__*/ _jsx(SpecraListPage, {
                "data-testid": Testids.awardCompare.workspaceRoot,
                "data-change-set-id": changeSetId,
                header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    "data-testid": Testids.awardCompare.summaryHeader,
                    title: "Award Comparison",
                    description: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "Change-set ",
                            /*#__PURE__*/ _jsx("code", {
                                className: "text-xs",
                                children: data.changeSetId
                            }),
                            " • State",
                            ' ',
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "outline",
                                children: data.state
                            }),
                            " • ",
                            data.discrepancyCount,
                            ' ',
                            "discrepancies (",
                            data.blockingCount,
                            " Blocking)"
                        ]
                    })
                }),
                toolbarProps: {
                    search: false,
                    filters: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(FilterSelect, {
                                testid: Testids.awardCompare.filterArea,
                                label: "Area",
                                value: areaFilter,
                                onChange: setAreaFilter,
                                options: [
                                    'all',
                                    ...AREA_ORDER
                                ],
                                placeholder: "All areas"
                            }),
                            /*#__PURE__*/ _jsx(FilterSelect, {
                                testid: Testids.awardCompare.filterSeverity,
                                label: "Severity",
                                value: severityFilter,
                                onChange: setSeverityFilter,
                                options: [
                                    'all',
                                    'Informational',
                                    'Moderate',
                                    'High',
                                    'Blocking'
                                ],
                                placeholder: "Any severity"
                            }),
                            /*#__PURE__*/ _jsx(FilterSelect, {
                                testid: Testids.awardCompare.filterResolution,
                                label: "Resolution",
                                value: resolutionFilter,
                                onChange: setResolutionFilter,
                                options: [
                                    'all',
                                    ...RESOLUTION_STATES
                                ],
                                placeholder: "Any resolution"
                            })
                        ]
                    })
                },
                footer: /*#__PURE__*/ _jsx(CommitFooter, {
                    gate: commitGate,
                    applied: data.state === 'Applied',
                    committing: commit.isPending,
                    onClick: onCommit
                }),
                children: /*#__PURE__*/ _jsxs(SpecraCard, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCardHeader, {
                            children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                children: "Discrepancies"
                            })
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCardContent, {
                            className: "space-y-3",
                            children: [
                                commitGate.openReview > 0 && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                                    tone: "warning",
                                    "data-testid": Testids.reviewItem.gateNote,
                                    "data-open-count": commitGate.openReview,
                                    children: gateNote(commitGate.openReview)
                                }),
                                /*#__PURE__*/ _jsx(ReviewItemList, {
                                    items: reviewItems,
                                    onOpen: (id)=>{
                                        setOpenReviewId(id);
                                        setReviewOpen(true);
                                    }
                                }),
                                /*#__PURE__*/ _jsx(SpecraDataTable, {
                                    caption: "Discrepancies by comparison area",
                                    testId: Testids.awardCompare.discrepancyTable,
                                    columns: DISCREPANCY_COLUMNS,
                                    groups: groups.map((group)=>({
                                            id: group.area,
                                            label: group.area,
                                            summary: group.rows.length,
                                            rows: group.rows,
                                            testId: Testids.awardCompare.areaGroupHeader,
                                            headerProps: {
                                                'data-area': group.area
                                            }
                                        })),
                                    getRowId: (d)=>d.id,
                                    getRowLabel: (d)=>d.narrative || d.targetSdsRecordType || 'Discrepancy',
                                    getRowActionLabel: (d)=>`Open ${d.narrative || 'discrepancy'}`,
                                    rowTestId: Testids.awardCompare.discrepancyRow,
                                    getRowProps: (d)=>({
                                            'data-discrepancy-id': d.id
                                        }),
                                    onRowClick: (d)=>openRow(d.id),
                                    emptyTitle: reviewItems.length > 0 && [
                                        areaFilter,
                                        severityFilter,
                                        resolutionFilter
                                    ].every((f)=>f === 'all') ? 'No other discrepancies.' : 'No discrepancies match the current filters.'
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: !!open,
                onOpenChange: (next)=>{
                    if (!next) setOpenId(null);
                },
                title: shownDiscrepancy?.comparisonArea ?? '',
                description: shownDiscrepancy?.targetSdsRecordType,
                testId: Testids.awardCompare.drawerRoot,
                width: "lg",
                footer: /*#__PURE__*/ _jsxs("footer", {
                    className: "flex shrink-0 items-center gap-2 border-t border-border bg-muted px-4 py-3 sm:px-5",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: handleResolve,
                            disabled: !canSubmit,
                            "data-testid": Testids.awardCompare.drawerSubmitButton,
                            children: resolve.isPending ? 'Saving…' : decision === 'Waived' || decision === 'Accepted' && shownDiscrepancy?.acknowledges ? rowDecisionLabel(shownDiscrepancy, decision) : `Mark ${decision}`
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            onClick: handleRoute,
                            disabled: !canRoute,
                            "data-testid": Testids.awardCompare.drawerRouteSubmitButton,
                            children: route.isPending ? 'Routing…' : `Re-route to ${newOwner}`
                        })
                    ]
                }),
                children: shownDiscrepancy ? // Keyed per row: a typed value never carries over to another row.
                /*#__PURE__*/ _jsx(DiscrepancyDrawer, {
                    discrepancy: shownDiscrepancy,
                    decision: decision,
                    onDecisionChange: setDecision,
                    rationale: rationale,
                    onRationaleChange: setRationale,
                    decidedJson: decidedJson,
                    onDecidedJsonChange: setDecidedJson,
                    newOwner: newOwner,
                    onNewOwnerChange: setNewOwner
                }, shownDiscrepancy.id) : null
            }),
            /*#__PURE__*/ _jsx(ReviewItemDrawer, {
                items: reviewItems,
                openId: openReviewId,
                open: reviewOpen,
                onClose: ()=>setReviewOpen(false),
                // No program exists until the import commits.
                programId: "",
                projectId: zouProjectId,
                changeSetId: changeSetId
            })
        ]
    });
}
function FilterSelect({ testid, label, value, options, placeholder, onChange }) {
    return /*#__PURE__*/ _jsx(SpecraSelect, {
        size: "sm",
        className: "w-44",
        "aria-label": label,
        value: value,
        onChange: (e)=>onChange(e.target.value),
        "data-testid": testid,
        children: options.map((o)=>/*#__PURE__*/ _jsx("option", {
                value: o,
                children: o === 'all' ? placeholder : resolutionLabel(o)
            }, o))
    });
}
// Known areas in SDS order, then any area the page doesn't know yet: a row
// the gate counts must never be dropped from the table.
export function groupByArea(disc) {
    const map = new Map();
    for (const d of disc){
        const existing = map.get(d.comparisonArea);
        if (existing) {
            existing.push(d);
        } else {
            map.set(d.comparisonArea, [
                d
            ]);
        }
    }
    const known = new Set(AREA_ORDER);
    const unknown = [
        ...map.keys()
    ].filter((a)=>!known.has(a)).sort();
    return [
        ...AREA_ORDER,
        ...unknown
    ].flatMap((area)=>{
        const rows = map.get(area);
        if (!rows || rows.length === 0) return [];
        return [
            {
                area: area || 'Other',
                rows
            }
        ];
    });
}
const DISCREPANCY_COLUMNS = [
    {
        id: 'narrative',
        header: 'Area / Narrative',
        cell: (d)=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            d.narrative || '(no narrative)',
                            /*#__PURE__*/ _jsx("span", {
                                onClick: (e)=>e.stopPropagation(),
                                children: /*#__PURE__*/ _jsx(ReviewSourceChip, {
                                    ...reviewSourceProps(d, {
                                        after: 'Awarded contract',
                                        before: 'Bid baseline'
                                    })
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        size: "meta",
                        tone: "muted",
                        children: d.targetSdsRecordType
                    }),
                    noWriterNeedsDecision(d) && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        size: "meta",
                        tone: "warning",
                        "data-testid": Testids.awardCompare.noWriterNote,
                        "data-item-id": d.id,
                        children: NO_WRITER_NOTE
                    })
                ]
            })
    },
    {
        id: 'severity',
        header: 'Severity',
        cell: (d)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                variant: SEVERITY_BADGE_VARIANTS[d.severity] ?? 'neutral',
                "data-testid": Testids.awardCompare.severityBadge,
                "data-severity": d.severity,
                children: d.severity
            })
    },
    {
        id: 'owner',
        header: 'Owner',
        cell: (d)=>/*#__PURE__*/ _jsx(SpecraTag, {
                "data-testid": Testids.awardCompare.ownerChip,
                "data-owner": d.functionalOwner,
                children: d.functionalOwner
            })
    },
    {
        id: 'resolution',
        header: 'Resolution',
        cell: (d)=>{
            const badge = /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: RESOLUTION_BADGE_VARIANTS[d.resolutionState] ?? 'neutral',
                "data-testid": Testids.awardCompare.resolutionStateBadge,
                "data-resolution": d.resolutionState,
                children: resolutionLabel(d.resolutionState, d.review)
            });
            return d.resolutionState === 'Acknowledged' ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: ACKNOWLEDGED_TOOLTIP,
                children: badge
            }) : badge;
        }
    },
    {
        id: 'open',
        header: /*#__PURE__*/ _jsx("span", {
            className: "sr-only",
            children: "Open"
        }),
        align: 'right',
        cell: ()=>/*#__PURE__*/ _jsx(SpecraText, {
                "aria-hidden": "true",
                size: "meta",
                tone: "muted",
                children: "Open →"
            })
    }
];
function DiscrepancyDrawer({ discrepancy, decision, onDecisionChange, rationale, onRationaleChange, decidedJson, onDecidedJsonChange, newOwner, onNewOwnerChange }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-[var(--space-card)] flex flex-wrap gap-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: SEVERITY_BADGE_VARIANTS[discrepancy.severity] ?? 'neutral',
                        children: discrepancy.severity
                    }),
                    /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "neutral",
                        children: [
                            "Owner: ",
                            discrepancy.functionalOwner
                        ]
                    }),
                    discrepancy.resolutionState === 'Acknowledged' ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                        content: ACKNOWLEDGED_TOOLTIP,
                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: RESOLUTION_BADGE_VARIANTS.Acknowledged,
                            children: resolutionLabel(discrepancy.resolutionState, discrepancy.review)
                        })
                    }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: RESOLUTION_BADGE_VARIANTS[discrepancy.resolutionState] ?? 'neutral',
                        children: resolutionLabel(discrepancy.resolutionState, discrepancy.review)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-[length:var(--fs-ui-body)] leading-[1.65] text-muted-foreground",
                children: discrepancy.narrative || '(no narrative emitted by the rule pack)'
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)] space-y-4",
                children: [
                    /*#__PURE__*/ _jsx(DiffPane, {
                        label: "Baseline",
                        value: discrepancy.baselineValueJson
                    }),
                    /*#__PURE__*/ _jsx(DiffPane, {
                        label: "Proposed",
                        value: discrepancy.proposedValueJson
                    }),
                    /*#__PURE__*/ _jsx(DiffPane, {
                        label: "Final contract",
                        value: discrepancy.finalContractValueJson
                    }),
                    hasReviewSource(discrepancy) && /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1 text-xs text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "Source"
                            }),
                            /*#__PURE__*/ _jsx(ReviewSourceChip, {
                                ...reviewSourceProps(discrepancy, {
                                    after: 'Awarded contract',
                                    before: 'Bid baseline'
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "mt-[var(--space-5-5)] space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Resolve"
                    }),
                    hasNoWriter(discrepancy) && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "warning",
                        "data-testid": Testids.awardCompare.noWriterNote,
                        "data-item-id": discrepancy.id,
                        children: NO_WRITER_NOTE
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Resolution",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            value: decision,
                            onChange: (e)=>onDecisionChange(e.target.value),
                            "data-testid": Testids.awardCompare.drawerDecisionSelect,
                            "data-no-writer": hasNoWriter(discrepancy) || undefined,
                            children: rowDecisions(discrepancy).map((s)=>/*#__PURE__*/ _jsx("option", {
                                    value: s,
                                    children: rowDecisionLabel(discrepancy, s)
                                }, s))
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Decision rationale",
                        required: true,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: rationale,
                            onChange: (e)=>onRationaleChange(e.target.value),
                            "data-testid": Testids.awardCompare.drawerRationaleInput
                        })
                    }),
                    /*#__PURE__*/ _jsx(DecidedValueField, {
                        discrepancy: discrepancy,
                        decision: decision,
                        decidedJson: decidedJson,
                        onDecidedJsonChange: onDecidedJsonChange
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "mt-[var(--space-5-5)] space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Re-route"
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Functional owner",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            value: newOwner,
                            onChange: (e)=>onNewOwnerChange(e.target.value),
                            "data-testid": Testids.awardCompare.drawerRouteSelect,
                            children: FUNCTIONAL_OWNERS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o,
                                    children: o
                                }, o))
                        })
                    })
                ]
            })
        ]
    });
}
// The reviewer's value. On a correct-only row it is required and typed — a
// date or an amount per part of the field's shape — and the JSON the writer
// reads is assembled here. Known shapes use these controls on every row;
// genuinely unknown shapes keep raw JSON behind a collapsed advanced control.
function DecidedValueField({ discrepancy, decision, decidedJson, onDecidedJsonChange }) {
    const field = rowField(discrepancy);
    const parts = correctedParts(field);
    const [typed, setTyped] = useState({});
    // Only Correct takes a value on a correct-only row (needsValue).
    if (discrepancy.correctOnly && decision !== 'Corrected') return null;
    if (!parts) {
        // Corrected must carry a value (needsValue), so its input is never hidden.
        if (decision === 'Corrected') {
            return /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Corrected value (JSON)",
                hint: "Use only when the value has no known shape.",
                required: true,
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "data-testid": Testids.awardCompare.drawerDecidedValueInput,
                    value: decidedJson,
                    onChange: (e)=>onDecidedJsonChange(e.target.value)
                })
            });
        }
        return /*#__PURE__*/ _jsxs("details", {
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "summary",
                    size: "body-ui",
                    tone: "muted",
                    className: "cursor-pointer",
                    children: "Advanced"
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    className: "mt-2",
                    label: "Decided value JSON",
                    hint: "Use only when the value has no known shape.",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        "data-testid": Testids.awardCompare.drawerDecidedValueInput,
                        value: decidedJson,
                        onChange: (e)=>onDecidedJsonChange(e.target.value)
                    })
                })
            ]
        });
    }
    const setPart = (key, value)=>{
        const next = {
            ...typed,
            [key]: value
        };
        setTyped(next);
        onDecidedJsonChange(correctedAwardJson(field, next));
    };
    const reversedDates = parts.length === 2 && parts.every((part)=>part.kind === 'date') && !!typed[parts[0].key] && !!typed[parts[1].key] && typed[parts[1].key] < typed[parts[0].key];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            parts.map((p)=>p.kind === 'date' ? /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: p.label,
                    required: true,
                    value: typed[p.key] ?? '',
                    onValueChange: (v)=>setPart(p.key, v),
                    "data-testid": Testids.awardCompare.drawerCorrectedInput,
                    "data-part": p.key
                }, p.key) : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: p.label,
                    required: true,
                    hint: "Dollars, as the award states it",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        type: "number",
                        step: "0.01",
                        min: "0",
                        inputMode: "decimal",
                        value: typed[p.key] ?? '',
                        onChange: (e)=>setPart(p.key, e.target.value),
                        "data-testid": Testids.awardCompare.drawerCorrectedInput,
                        "data-part": p.key
                    })
                }, p.key)),
            reversedDates ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "danger",
                children: "The end date is before the start date."
            }) : null
        ]
    });
}
function DiffPane({ label, value }) {
    const display = useMemo(()=>{
        try {
            const parsed = JSON.parse(value);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                const formatted = Object.fromEntries(Object.entries(parsed).map(([key, item])=>[
                        key,
                        isMoneyField(key) && typeof item === 'number' ? formatMoney(item) : item
                    ]));
                return JSON.stringify(formatted, null, 2);
            }
        } catch  {
        // Fall through to the existing plain-text behavior.
        }
        return prettyJSON(value);
    }, [
        value
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                headingAs: "h4",
                title: label
            }),
            /*#__PURE__*/ _jsx("pre", {
                className: "mt-[var(--space-2)] text-xs bg-muted rounded p-2 whitespace-pre-wrap break-words",
                children: display || '(empty)'
            })
        ]
    });
}
function prettyJSON(blob) {
    if (!blob) return '';
    try {
        return JSON.stringify(JSON.parse(blob), null, 2);
    } catch  {
        return blob;
    }
}
export function computeCommitGate(discrepancies) {
    // Mirrors zou's validateCommitInvariants (commit_import.go): Open/Routed are
    // unsettled; a Blocking row must carry a settled disposition. Review items
    // are Blocking, so each one not settled holds the commit under one rule or
    // the other; they are counted apart so the note can name them.
    const SETTLED = new Set([
        'Accepted',
        'Corrected',
        'Waived',
        'Closed',
        'Applied',
        'Acknowledged'
    ]);
    let openReview = 0;
    let openCount = 0;
    let unresolvedBlocking = 0;
    for (const d of discrepancies){
        if (isReviewItem(d)) {
            const held = d.resolutionState === 'Open' || d.resolutionState === 'Routed' || d.severity === 'Blocking' && !SETTLED.has(d.resolutionState);
            if (held) openReview++;
            continue;
        }
        if (d.resolutionState === 'Open' || d.resolutionState === 'Routed' || noWriterNeedsDecision(d)) {
            openCount++;
        } else if (d.severity === 'Blocking' && !SETTLED.has(d.resolutionState) && d.resolutionState !== 'Rejected') {
            unresolvedBlocking++;
        }
    }
    // A zero-discrepancy change-set is a valid commit target — an award
    // that perfectly matched the bid baseline shouldn't be locked out of
    // CommitImport just because the engine produced nothing. Earlier this
    // gate also required `discrepancies.length > 0`, which trapped clean
    // comparisons in a permanent disabled state.
    return {
        ready: openReview === 0 && openCount === 0 && unresolvedBlocking === 0,
        openReview,
        openCount,
        unresolvedBlocking
    };
}
// The footer's one sentence: every reason the commit is off, or ready.
export function commitGateNote(gate) {
    if (gate.ready) return 'All discrepancies resolved. Ready to commit.';
    const plural = (n, one, many)=>n === 1 ? one : many;
    const parts = [];
    if (gate.openReview > 0) {
        parts.push(`${gate.openReview} ${plural(gate.openReview, 'fact needs', 'facts need')} your call`);
    }
    if (gate.openCount > 0) {
        parts.push(`${gate.openCount} ${plural(gate.openCount, 'discrepancy is', 'discrepancies are')} still open`);
    }
    if (gate.unresolvedBlocking > 0) {
        parts.push(`${gate.unresolvedBlocking} Blocking ${plural(gate.unresolvedBlocking, 'discrepancy needs', 'discrepancies need')} a settled decision`);
    }
    return `Commit is off: ${parts.join('; ')}.`;
}
export const ALREADY_APPLIED_NOTE = 'Already applied — this import created the contract program.';
function CommitFooter({ gate, applied, committing, readOnly, onClick }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "sticky bottom-0 flex items-center justify-between gap-4 border-t border-border bg-background py-3",
        "data-testid": Testids.awardCompare.commitFooter,
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                size: "caption",
                tone: "muted",
                "data-testid": Testids.awardCompare.commitGateNote,
                "data-ready": gate.ready && !applied,
                "data-applied": applied,
                children: applied ? ALREADY_APPLIED_NOTE : commitGateNote(gate)
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: onClick,
                disabled: applied || !gate.ready || committing || readOnly,
                title: applied ? ALREADY_APPLIED_NOTE : undefined,
                "data-testid": Testids.awardCompare.commitButton,
                children: committing ? 'Committing…' : 'Commit Import'
            })
        ]
    });
}
